'use client';

import { useCallback, useEffect, useState, type ComponentType } from 'react';
import type { AdSlotProps } from './AdSlot';
import { isLabPerformanceAgent, whenThirdPartyAllowed } from '@/lib/defer-third-party';

type AdSlotWrapperProps = AdSlotProps;

/**
 * Reklam yüklenene kadar aynı yüksekliği SSR'dan itibaren rezerve eder;
 * reklam geldiğinde içerik aşağı itilmez (CLS). Slot "boş" sonuçlanırsa
 * (dolmadı / kapalı / premium) rezerv bırakılır ve alan kapanır.
 */
const RESERVE_MIN_H: Record<'horizontal' | 'square' | 'vertical', number> = {
  horizontal: 100,
  square: 250,
  vertical: 250,
};

export default function AdSlotWrapper(props: AdSlotWrapperProps) {
  const [Slot, setSlot] = useState<ComponentType<AdSlotProps> | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    if (isLabPerformanceAgent()) return;

    let cancelled = false;
    void whenThirdPartyAllowed({ fallbackMs: 3_500 })
      .then(() => import('./AdSlot'))
      .then((module) => {
        if (!cancelled) setSlot(() => module.AdSlot);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleLoadState = useCallback((state: 'idle' | 'filled' | 'empty') => {
    if (state === 'empty') setCollapsed(true);
    else if (state === 'filled') setCollapsed(false);
  }, []);

  return (
    <div
      style={
        collapsed
          ? undefined
          : { minHeight: RESERVE_MIN_H[props.format ?? 'horizontal'] }
      }
    >
      {Slot ? <Slot {...props} onLoadState={handleLoadState} /> : null}
    </div>
  );
}
